import { useState, useEffect, useCallback } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import {
  House, Buildings, Users, Target, Phone, ClockClockwise,
  Storefront, UserCircle, Gear, SignOut, List, X,
  UsersThree, EnvelopeSimple, MagnifyingGlass, Receipt, Calculator,
  UsersFour, ToggleRight, Kanban, Calendar, FunnelSimple, Cube,
  ChartBar, Lightning, Crosshair, TreeStructure, FileText, GraduationCap,
  UserPlus, Notebook, SunHorizon, ChatCircle, Megaphone, FolderOpen, ArrowsDownUp,
  Pulse, Moon, Sun, Tray
} from "@phosphor-icons/react";
import NotificationBell from "@/components/NotificationBell";

const ALL = ["admin", "team_leader", "opportunity_finder", "candidate_finder", "senior_recruitment_partner"];
const TL_ADMIN = ["admin", "team_leader"];

const NAV_ITEMS = [
  { path: "/hub", label: "Dashboard", icon: House, roles: ALL },
  { path: "/hub/intelligence", label: "Talent Engine", icon: Lightning, roles: TL_ADMIN },
  { path: "/hub/candidate-engine", label: "Candidate Engine", icon: Crosshair, roles: TL_ADMIN },
  { path: "/hub/messaging", label: "Messages", icon: ChatCircle, roles: ALL },
  { path: "/hub/briefings", label: "Briefings", icon: Megaphone, roles: ALL },
  { path: "/hub/planner", label: "Daily Planner", icon: SunHorizon, roles: ALL },
  { path: "/hub/sector-hub", label: "Sector Hub", icon: Crosshair, roles: ALL },
  { path: "/hub/workflow", label: "Workflow", icon: TreeStructure, roles: ALL },
  { path: "/hub/vacancies", label: "Vacancies", icon: Cube, roles: ALL },
  { path: "/hub/pipeline", label: "Pipeline", icon: Kanban, roles: ALL },
  { path: "/hub/interviews", label: "Interviews", icon: Calendar, roles: ALL },
  { path: "/hub/interview-pipeline", label: "Int. Pipeline", icon: FunnelSimple, roles: ALL },
  { path: "/hub/companies", label: "Companies", icon: Buildings, roles: ALL },
  { path: "/hub/candidates", label: "Candidates", icon: Users, roles: ALL },
  { path: "/hub/leads", label: "Leads", icon: Target, roles: ALL },
  { path: "/hub/enquiries", label: "Website Enquiries", icon: Tray, roles: TL_ADMIN },
  { path: "/hub/teams", label: "Teams", icon: UsersThree, roles: ALL },
  { path: "/hub/team-resources", label: "Resources", icon: FolderOpen, roles: ALL },
  { path: "/hub/distribution", label: "Distribute", icon: ArrowsDownUp, roles: ALL },
  { path: "/hub/team-status", label: "Team Status", icon: Pulse, roles: TL_ADMIN },
  { path: "/hub/emails", label: "Email", icon: EnvelopeSimple, roles: ALL },
  { path: "/hub/search", label: "Search Centre", icon: MagnifyingGlass, roles: ALL },
  { path: "/hub/talent-pools", label: "Talent Pools", icon: UsersFour, roles: ALL },
  { path: "/hub/dialer", label: "Dialer", icon: Phone, roles: ALL },
  { path: "/hub/call-logs", label: "Call Logs", icon: ClockClockwise, roles: ALL },
  { path: "/hub/scripts", label: "Scripts", icon: Notebook, roles: ALL },
  { path: "/hub/analytics", label: "Analytics", icon: ChartBar, roles: ["admin"] },
  { path: "/hub/activity-log", label: "Activity Log", icon: Lightning, roles: TL_ADMIN },
  { path: "/hub/commission", label: "Commissions", icon: Calculator, roles: ALL },
  { path: "/hub/invoices", label: "Invoices", icon: Receipt, roles: ["admin"] },
  { path: "/hub/contracts", label: "Contracts", icon: FileText, roles: ALL },
  { path: "/hub/academy", label: "Academy", icon: GraduationCap, roles: ALL },
  { path: "/hub/academy-admin", label: "Academy Admin", icon: GraduationCap, roles: ["admin"] },
  { path: "/hub/onboarding-admin", label: "Onboarding", icon: UserPlus, roles: ["admin"] },
  { path: "/hub/telephony-settings", label: "Telephony", icon: Phone, roles: ["admin"] },
  { path: "/hub/feature-toggles", label: "Features", icon: ToggleRight, roles: ["admin"] },
  { path: "/hub/tenants", label: "Franchises", icon: Storefront, roles: ["admin"] },
  { path: "/hub/users", label: "Users", icon: UserCircle, roles: ["admin"] },
  { path: "/hub/settings", label: "Settings", icon: Gear, roles: ["admin"] },
  { path: "/hub/partnership-pricing", label: "Partnerships", icon: Calculator, roles: ["admin"] },
];

const MOBILE_NAV = [
  { path: "/hub", label: "Home", icon: House },
  { path: "/hub/sector-hub", label: "Sector", icon: Crosshair },
  { path: "/hub/vacancies", label: "Vacancies", icon: Cube },
  { path: "/hub/pipeline", label: "Pipeline", icon: Kanban },
  { path: "/hub/settings", label: "More", icon: Gear },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [emailUnread, setEmailUnread] = useState(0);
  const badgeFor = (p) => (p === "/messaging" ? unreadCount : p === "/hub/emails" ? emailUnread : 0);
  const [dark, setDark] = useState(() => localStorage.getItem("bm-dark") === "1");
  const role = user?.role || "opportunity_finder";
  const filteredNav = NAV_ITEMS.filter((item) => item.roles.includes(role));

  const roleLabel = { admin: "Administrator", team_leader: "Team Leader", opportunity_finder: "Opportunity Finder", candidate_finder: "Candidate Finder", senior_recruitment_partner: "Senior Recruitment Partner" }[role] || role;

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("bm-dark", dark ? "1" : "0");
  }, [dark]);

  // Poll unread count
  const fetchUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/messaging/unread-count");
      setUnreadCount(data.unread_count || 0);
    } catch { /* ignore */ }
    try {
      const { data } = await api.get("/emails/unread-count");
      setEmailUnread(data.unread_count || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 10000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  return (
    <div className="flex h-screen bg-[#F9FAFB] dark:bg-[#0F172A] overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-[240px] bg-white dark:bg-[#1E293B] border-r border-[#E5E7EB] dark:border-[#334155] flex-shrink-0">
        <div className="flex items-center gap-3 px-5 py-5 border-b border-[#E5E7EB]">
          <div className="bm-logo-mark">BM</div>
          <div>
            <div className="bm-logo-text text-[15px]">Banks Morgan</div>
            <div className="text-[10px] text-[#6B7280] font-medium tracking-wide uppercase">Recruitment Hub</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
          {filteredNav.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/hub"}
              className={({ isActive }) => `bm-sidebar-item ${isActive ? "active" : ""}`}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              <div className="relative">
                <item.icon size={20} weight={location.pathname === item.path || (item.path !== "/hub" && location.pathname.startsWith(item.path)) ? "duotone" : "regular"} />
                {badgeFor(item.path) > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-[#EF4444] text-white text-[8px] font-bold min-w-[16px] h-[16px] rounded-full flex items-center justify-center px-0.5 shadow-sm" data-testid="unread-badge">
                    {badgeFor(item.path) > 99 ? "99+" : badgeFor(item.path)}
                  </span>
                )}
              </div>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="px-4 py-4 border-t border-[#E5E7EB]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-[#0F172A] flex items-center justify-center text-white text-xs font-bold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-[#0F172A] truncate">{user?.name}</div>
              <div className="text-[10px] text-[#C5A059] font-medium uppercase">{roleLabel}</div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 text-[#6B7280] hover:text-[#EF4444] hover:bg-red-50"
            onClick={logout}
            data-testid="logout-btn"
          >
            <SignOut size={16} /> Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/30" onClick={() => setSidebarOpen(false)} />
          <div className="relative w-[280px] bg-white shadow-xl flex flex-col animate-slide-in">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E7EB]">
              <div className="flex items-center gap-3">
                <div className="bm-logo-mark">BM</div>
                <span className="bm-logo-text text-[15px]">Banks Morgan</span>
              </div>
              <button onClick={() => setSidebarOpen(false)} data-testid="close-mobile-sidebar">
                <X size={20} className="text-[#6B7280]" />
              </button>
            </div>
            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
              {filteredNav.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === "/hub"}
                  className={({ isActive }) => `bm-sidebar-item ${isActive ? "active" : ""}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <div className="relative">
                    <item.icon size={20} weight="regular" />
                    {badgeFor(item.path) > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 bg-[#EF4444] text-white text-[8px] font-bold min-w-[16px] h-[16px] rounded-full flex items-center justify-center px-0.5 shadow-sm">
                        {badgeFor(item.path) > 99 ? "99+" : badgeFor(item.path)}
                      </span>
                    )}
                  </div>
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </nav>
            <div className="px-4 py-4 border-t border-[#E5E7EB]">
              <Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-[#6B7280]" onClick={logout}>
                <SignOut size={16} /> Sign Out
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <header className="h-14 bg-white dark:bg-[#1E293B] border-b border-[#E5E7EB] dark:border-[#334155] flex items-center px-4 md:px-6 flex-shrink-0">
          <button className="md:hidden mr-3" onClick={() => setSidebarOpen(true)} data-testid="open-mobile-sidebar">
            <List size={22} className="text-[#0F172A]" />
          </button>
          <div className="md:hidden flex items-center gap-2">
            <div className="bm-logo-mark text-[11px] w-7 h-7">BM</div>
            <span className="bm-logo-text text-[13px]">Banks Morgan</span>
          </div>
          <div className="flex-1" />
          <a href="tel:02045850050" className="hidden md:flex items-center gap-1.5 text-sm text-[#6B7280] dark:text-[#9CA3AF] hover:text-[#0F172A] dark:hover:text-white transition-colors mr-2" data-testid="hub-phone-number">
            <Phone size={15} weight="bold" className="text-[#C5A059]" />
            <span className="font-medium">020 4585 0050</span>
          </a>
          <button onClick={() => setDark(d => !d)} className="hidden md:flex items-center justify-center w-8 h-8 rounded-full hover:bg-[#F3F4F6] dark:hover:bg-[#374151] transition-colors mr-1" data-testid="dark-mode-toggle" title={dark ? "Light mode" : "Dark mode"}>
            {dark ? <Sun size={16} className="text-[#C5A059]" /> : <Moon size={16} className="text-[#6B7280]" />}
          </button>
          <div className="md:hidden">
            <NotificationBell testIdPrefix="mobile" />
          </div>
          <div className="hidden md:flex items-center gap-3 text-sm text-[#6B7280]">
            <NotificationBell testIdPrefix="desktop" />
            <span className="text-[#0F172A] font-medium">{user?.name}</span>
            <span className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase bg-[#0F172A] text-[#C5A059]">{roleLabel}</span>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6 pb-20 md:pb-6">
          <div className="bm-page-enter">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile Bottom Nav */}
      <div className="bm-mobile-nav md:hidden">
        {MOBILE_NAV.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === "/"}
            className={({ isActive }) => `bm-mobile-nav-item ${isActive ? "active" : ""}`}
            data-testid={`mobile-nav-${item.label.toLowerCase()}`}
          >
            <item.icon size={22} weight={location.pathname === item.path ? "duotone" : "regular"} className="bm-nav-icon" />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </div>
    </div>
  );
}
